import { Modal } from "@wrstudios/components";
import { formatCurrency, formatNumber, getDateShort } from "@wrstudios/utils";
import React, { useState } from "react";
import Helmet from "react-helmet";
import {
  getAveragePrice,
  getAverageZestimate,
  getAverageZestimatePriceDifferencePercentage,
  getHighestZestimateDifferences
} from "../../../utils/listings";
import { translate } from "../../../utils/locale";
import Loading from "../../common/Loading";
import {
  Table,
  TableData,
  TableFooter,
  TableHeading
} from "../../common/Table";
import Wistia from "../../common/Wistia";
import LiveHeader from "../common/LiveHeader";
import IconInfo from "../icons/IconInfo";
import LiveListingDetailsModal from "../listing/LiveListingDetailsModal";
import { useLive } from "../LiveProvider";
import zillow from "./images/zillow.png";
import LiveOnlineValuationsChart from "./LiveOnlineValuationsChart";
import {
  Badge,
  Cell,
  CityStateZip,
  Container,
  Description,
  Disclaimer,
  Divider,
  DividerCell,
  Grid,
  Group,
  GroupTitle,
  Header,
  Info,
  ModalContent,
  ModalText,
  Street,
  SubTitle,
  TableHeader,
  TableRow,
  Title,
  TitleContainer,
  Value,
  Zestimate,
  ZillowLogo
} from "./styled/live-online-valuations";

function LiveOnlineValuations() {
  const { visibleListings, visibleListingGroups } = useLive();
  const [previewListingId, setPreviewListingId] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const { priceDifference, priceDifferencePercentage } =
    getHighestZestimateDifferences(visibleListings);

  return (
    <>
      <Helmet>
        <title>Online Valuations</title>
      </Helmet>
      <LiveHeader>Online Valuations</LiveHeader>
      <Container>
        <Header>
          <SubTitle className="page-sub-title">
            How accurate are zestimates?
          </SubTitle>
          <TitleContainer>
            <Grid>
              <Cell>
                <Title className="font-primary page-title">
                  {priceDifferencePercentage
                    ? `${formatNumber(
                        Math.abs(priceDifferencePercentage),
                        "0[,][.]0"
                      )}%`
                    : "--%"}
                </Title>
              </Cell>
              <DividerCell>
                <Divider />
              </DividerCell>
              <Cell>
                <Title className="font-primary page-title">
                  {priceDifference
                    ? formatCurrency(Math.abs(priceDifference))
                    : "$---"}
                </Title>
              </Cell>
            </Grid>
            <Info onClick={() => setShowInfoModal(true)}>
              <IconInfo />
            </Info>
          </TitleContainer>
          <Description className="page-description">
            Zestimates varied up to{" "}
            <Value className="page-description-value">
              {priceDifferencePercentage
                ? `${formatNumber(
                    Math.abs(priceDifferencePercentage),
                    "0.[0]"
                  )}%`
                : "--%"}
            </Value>{" "}
            or{" "}
            <Value className="page-description-value">
              {priceDifference
                ? formatCurrency(Math.abs(priceDifference))
                : "$---"}
            </Value>{" "}
            compared to actual {translate("mls.label", "MLS")} prices.
          </Description>
        </Header>
        {Object.entries(visibleListingGroups).map(([status, listings]) => (
          <LiveGroup
            key={status}
            status={status}
            listings={listings}
            setPreviewListingId={setPreviewListingId}
          />
        ))}
        <Disclaimer>
          Click Zestimate price to view details on Zillow. Zestimate data is
          provided "as is" via the Zestimate API. &copy; Zillow, Inc., 2006-
          {new Date().getUTCFullYear()}. Use is subject to Terms of Use.
        </Disclaimer>
        <ZillowLogo src={zillow} alt="Zillow Logo" />
      </Container>
      {showInfoModal && (
        <Modal onClose={() => setShowInfoModal(false)}>
          <ModalContent>
            <Modal.Header className="font-primary modal-header">
              How accurate are Zestimates?
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <ModalText className="modal-text">
                Online home valuations like Zestimates are interesting metric
                for homeowners to ballpark the value of their home. Although
                they have gotten more accurate over the years, it's important to
                realize that sometimes these estimates can vary widely from
                market value. There's still nothing like a professional home
                appraisal to estimate the real value of a home.
              </ModalText>
              <Wistia videoId="r3qjeyp9ah" autoplay={false} />
            </Modal.Body>
          </ModalContent>
        </Modal>
      )}
      {previewListingId && (
        <LiveListingDetailsModal
          listingRecordId={previewListingId}
          onClose={() => setPreviewListingId(null)}
        />
      )}
    </>
  );
}

function LiveGroup({ status, listings, setPreviewListingId }) {
  const { zestimateRequest } = useLive();
  const [hoverListingId, setHoverListingId] = useState(null);
  const averagePrice = getAveragePrice(listings);
  const averageZestimate = getAverageZestimate(listings);
  const averageZestimatePriceDifferencePercentage =
    getAverageZestimatePriceDifferencePercentage(listings);
  const areAnyListingsSold = listings.some((listing) => listing.soldPrice);

  return (
    <Group key={status}>
      <GroupTitle className="group-title-online-valuations">
        <div>
          <Badge
            style={{
              backgroundColor: (listings[0] || { statusColor: "#000" })
                .statusColor
            }}>
            {listings.length}
          </Badge>
          {status}
        </div>
        {zestimateRequest.loading && <Loading />}
      </GroupTitle>
      <LiveOnlineValuationsChart
        listings={listings}
        hoverListingId={hoverListingId}
        setHoverListingId={setHoverListingId}
        setPreviewListingId={setPreviewListingId}
      />
      <Table>
        <TableHeader>
          <TableRow>
            <TableHeading className="table-heading-online-valuations">
              Address
            </TableHeading>
            <TableHeading className="table-heading-online-valuations">
              {areAnyListingsSold ? "Sold" : "List"} Date
            </TableHeading>
            <TableHeading className="table-heading-online-valuations">
              {areAnyListingsSold ? "Sold" : "List"} Price
            </TableHeading>
            <TableHeading className="table-heading-online-valuations">
              Zestimate
            </TableHeading>
            <TableHeading className="table-heading-online-valuations">
              Difference
            </TableHeading>
          </TableRow>
        </TableHeader>
        <tbody>
          {listings.map((listing) => (
            <TableRow
              key={listing.recordId}
              isGhost={listing.isGhost}
              onMouseEnter={() => setHoverListingId(listing.recordId)}
              onMouseLeave={() => setHoverListingId(null)}>
              <TableData>
                <Street
                  className="live-table-value"
                  onClick={() => setPreviewListingId(listing.recordId)}>
                  {listing.street}{" "}
                  <CityStateZip>{listing.cityStateZip}</CityStateZip>
                </Street>
              </TableData>
              <TableData>
                {areAnyListingsSold ? (
                  <>{listing.soldDate ? getDateShort(listing.soldDate) : "-"}</>
                ) : (
                  <>{listing.listDate ? getDateShort(listing.listDate) : "-"}</>
                )}
              </TableData>
              <TableData>
                {listing.price ? formatCurrency(listing.price) : "-"}
              </TableData>
              <TableData>
                {listing.zestimate.value ? (
                  <Zestimate href={listing.zestimate.url} target="_blank">
                    {formatCurrency(listing.zestimate.value)}
                  </Zestimate>
                ) : (
                  "-"
                )}
              </TableData>
              <TableData
                title={
                  !!listing.zestimate.priceDifferencePercentage
                    ? listing.zestimate.priceDifferencePercentage
                    : ""
                }>
                {listing.zestimate.priceDifferencePercentage
                  ? `${formatNumber(
                      listing.zestimate.priceDifferencePercentage,
                      "0[.]0"
                    )}%`
                  : "-"}
              </TableData>
            </TableRow>
          ))}
        </tbody>
        <TableFooter>
          <TableRow>
            <TableData className="live-table-value">Averages</TableData>
            <TableData className="live-table-value">-</TableData>
            <TableData className="live-table-value">
              {averagePrice ? formatCurrency(averagePrice) : "-"}
            </TableData>
            <TableData className="live-table-value">
              {averageZestimate ? formatCurrency(averageZestimate) : "-"}
            </TableData>
            <TableData className="live-table-value">
              {averageZestimatePriceDifferencePercentage
                ? `${formatNumber(
                    averageZestimatePriceDifferencePercentage,
                    "0[.]0"
                  )}%`
                : "-"}
            </TableData>
          </TableRow>
        </TableFooter>
      </Table>
    </Group>
  );
}

export default LiveOnlineValuations;
